import {
    type KeyboardEvent as ReactKeyboardEvent,
    type MouseEvent as ReactMouseEvent,
    useState,
    useCallback,
    useEffect,
    useRef
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { trpc } from '@/api/trpc';

interface ChatCommandPaletteProps {
    isOpen: boolean;
    onClose: () => void;
}

export function ChatCommandPalette({ isOpen, onClose }: ChatCommandPaletteProps) {
    // Mount the dialog only while open so each open starts with fresh query/selection
    // state instead of resetting it in an effect.
    if (!isOpen) return null;

    return <ChatCommandPaletteDialog onClose={onClose} />;
}

function ChatCommandPaletteDialog({ onClose }: { onClose: () => void }) {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);

    const sessionsQuery = useQuery({
        ...trpc.agent.searchSessions.queryOptions({ query, limit: 10 }),
        enabled: query.length > 1
    });

    const messagesQuery = useQuery({
        ...trpc.agent.searchMessages.queryOptions({ query, limit: 10 }),
        enabled: query.length > 2
    });

    const sessionResults = sessionsQuery.data?.sessions ?? [];
    const messageResults = messagesQuery.data?.messages ?? [];
    const totalResults = sessionResults.length + messageResults.length;

    useEffect(() => {
        const frame = requestAnimationFrame(() => inputRef.current?.focus());
        return () => cancelAnimationFrame(frame);
    }, []);

    const handleQueryChange = useCallback((value: string) => {
        setQuery(value);
        setSelectedIndex(0);
    }, []);

    useEffect(() => {
        if (!resultsRef.current) return;
        const selected = resultsRef.current.querySelector('[aria-selected="true"]');
        if (selected) selected.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    const selectSession = useCallback(
        (sessionId: number) => {
            onClose();
            void navigate(`/user/chat/${sessionId}`);
        },
        [navigate, onClose]
    );

    const selectMessage = useCallback(
        (sessionId: number, messageId: number) => {
            onClose();
            void navigate(`/user/chat/${sessionId}?highlight=${messageId}`);
        },
        [navigate, onClose]
    );

    const getActiveDescendantId = () => {
        if (totalResults === 0) return undefined;

        if (selectedIndex < sessionResults.length) {
            const session = sessionResults[selectedIndex];
            return session ? `cmd-session-${session.id}` : undefined;
        }

        const msgIdx = selectedIndex - sessionResults.length;
        const msg = messageResults[msgIdx];
        return msg ? `cmd-message-${msg.sessionId}-${msg.messageId}` : undefined;
    };

    const handleKeyDown = useCallback(
        (e: ReactKeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
                return;
            }

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (totalResults === 0) return;
                setSelectedIndex((prev) => Math.min(prev + 1, totalResults - 1));
                return;
            }

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (totalResults === 0) return;
                setSelectedIndex((prev) => Math.max(prev - 1, 0));
                return;
            }

            if (e.key === 'Enter' && totalResults > 0) {
                e.preventDefault();

                if (selectedIndex < sessionResults.length) {
                    const session = sessionResults[selectedIndex];
                    if (session) selectSession(session.id);
                } else {
                    const msgIdx = selectedIndex - sessionResults.length;
                    const msg = messageResults[msgIdx];
                    if (msg) selectMessage(msg.sessionId, msg.messageId);
                }
            }
        },
        [messageResults, onClose, selectMessage, selectSession, selectedIndex, sessionResults, totalResults]
    );

    const handleBackdropClick = (e: ReactMouseEvent<HTMLDivElement>) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
            onClose();
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]" role="presentation" onClick={handleBackdropClick}>
            <div className="fixed inset-0 bg-black/60" />

            <div
                className="relative w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden"
                ref={dialogRef}
                role="dialog"
            >
                <div className="px-4 py-3 border-b border-zinc-800">
                    <input
                        aria-activedescendant={getActiveDescendantId()}
                        aria-controls="command-palette-results"
                        aria-expanded={totalResults > 0}
                        className="w-full bg-transparent text-white text-sm placeholder-zinc-500 outline-none"
                        placeholder="Search chats and messages..."
                        ref={inputRef}
                        role="combobox"
                        type="text"
                        value={query}
                        onChange={(e) => handleQueryChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                {query.length > 1 && (
                    <div className="max-h-80 overflow-auto" id="command-palette-results" ref={resultsRef} role="listbox">
                        {sessionResults.length > 0 && (
                            <div>
                                <div className="px-4 pt-2 pb-1 text-xs font-medium text-zinc-500 uppercase tracking-wider">Sessions</div>
                                {sessionResults.map((s, i) => (
                                    <button
                                        aria-selected={i === selectedIndex}
                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-zinc-800 ${i === selectedIndex ? 'bg-zinc-800' : ''}`}
                                        id={`cmd-session-${s.id}`}
                                        key={s.id}
                                        role="option"
                                        type="button"
                                        onClick={() => selectSession(s.id)}
                                    >
                                        <div className="text-white truncate">{s.title || `Chat #${s.id}`}</div>
                                        <div className="text-xs text-zinc-500">{dayjs(s.updatedAt).fromNow()}</div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {messageResults.length > 0 && (
                            <div>
                                <div className="px-4 pt-2 pb-1 text-xs font-medium text-zinc-500 uppercase tracking-wider">Messages</div>
                                {messageResults.map((m, i) => {
                                    const globalIndex = sessionResults.length + i;
                                    return (
                                        <button
                                            aria-selected={globalIndex === selectedIndex}
                                            className={`w-full text-left px-4 py-2 text-sm hover:bg-zinc-800 ${globalIndex === selectedIndex ? 'bg-zinc-800' : ''}`}
                                            id={`cmd-message-${m.sessionId}-${m.messageId}`}
                                            key={`${m.sessionId}-${m.messageId}`}
                                            role="option"
                                            type="button"
                                            onClick={() => selectMessage(m.sessionId, m.messageId)}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-zinc-500 shrink-0">{m.role}</span>
                                                <span className="text-white truncate">{m.snippet}</span>
                                            </div>
                                            <div className="text-xs text-zinc-500 mt-0.5">
                                                {m.sessionTitle || `Chat #${m.sessionId}`} &middot; {dayjs(m.createdAt).fromNow()}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {sessionResults.length === 0 && messageResults.length === 0 && !sessionsQuery.isLoading && (
                            <div className="px-4 py-6 text-sm text-zinc-500 text-center">No results found.</div>
                        )}

                        {(sessionsQuery.isLoading || messagesQuery.isLoading) && (
                            <div className="px-4 py-3 text-sm text-zinc-500 text-center">Searching...</div>
                        )}
                    </div>
                )}

                <div className="px-4 py-2 border-t border-zinc-800 flex gap-4 text-xs text-zinc-500">
                    <span>
                        <kbd className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-400">↑↓</kbd> navigate
                    </span>
                    <span>
                        <kbd className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-400">↵</kbd> open
                    </span>
                    <span>
                        <kbd className="px-1 py-0.5 rounded bg-zinc-800 text-zinc-400">esc</kbd> close
                    </span>
                </div>
            </div>
        </div>,
        document.body
    );
}
